/*
 * Rutarea si paza ecranelor.
 *
 * BrowserRouter: adrese curate, fara diez. `/catalog/30` se poate trimite pe
 * mail, se poate pune la favorite si arata a adresa, nu a fragment.
 *
 * Pretul, si trebuie stiut la instalare: serverul care serveste fisierele
 * TREBUIE sa trimita catre index.html orice cale care nu e fisier. Fara regula
 * aia, "/requests/17" cerut direct - F5, sau un link primit - da 404, pentru ca
 * serverul cauta un fisier cu numele acela.
 *
 * Regula e in README, si e pusa deja pe vhostul de dezvoltare. Daca aplicatia
 * ajunge pe un server unde nu se poate configura asa ceva, se revine la
 * HashRouter schimband un singur cuvant in main.tsx.
 *
 * In carcasa nativa se foloseste deja HashRouter, ales in main.tsx dupa
 * platforma: acolo fisierele vin dintr-un director impachetat in aplicatie, deci
 * nu exista server care sa aplice regula de mai sus.
 */

import { useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { AppShell } from './components/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CountsProvider } from './components/CountsProvider';
import { Loading } from './components/states';
import { CartScreen } from './screens/CartScreen';
import { CatalogItemScreen } from './screens/CatalogItemScreen';
import { CatalogScreen } from './screens/CatalogScreen';
import { HelpArticleScreen } from './screens/HelpArticleScreen';
import { HelpScreen } from './screens/HelpScreen';
import { LoginScreen } from './screens/LoginScreen';
import { NewTicketScreen } from './screens/NewTicketScreen';
import { RequestDetailScreen } from './screens/RequestDetailScreen';
import { RequestsScreen } from './screens/RequestsScreen';
import { TicketDetailScreen } from './screens/TicketDetailScreen';
import { onBackButton } from './lib/platform';

/*
 * Butonul fizic de inapoi, pe Android.
 *
 * Fara el, o apasare inchide aplicatia din orice ecran - inclusiv din mijlocul
 * unei cereri pe jumatate completata. Cu el, se comporta ca peste tot: intai
 * urca prin ecrane, si abia din primul iese.
 *
 * Pe web nu se abonzeaza la nimic si nu costa nimic.
 */
function useAndroidBack(): void {
  const navigate = useNavigate();

  useEffect(
    () =>
      onBackButton((canGoBack) => {
        if (canGoBack) {
          navigate(-1);
          return;
        }

        /* Fara istoric, comportamentul asteptat pe Android e sa iasa. Importul e
           aici, nu sus, ca modulul nativ sa nu ajunga in bundle-ul de web. */
        void import('@capacitor/app').then(({ App: NativeApp }) => {
          void NativeApp.exitApp();
        });
      }),
    [navigate],
  );
}

export function App() {
  useAndroidBack();

  const { phase, authenticated } = useAuth();

  /*
   * Cat tine pornirea nu se deseneaza nici aplicatia, nici loginul. Pornirea
   * inseamna ori verificarea sesiunii restaurate din sessionStorage, ori
   * schimbul biletului de SSO - amandoua sunt cereri catre api. Fara pauza asta,
   * omul care are sesiune buna vede o clipa ecranul de login, iar cel care se
   * intoarce din portal vede loginul si crede ca SSO-ul a esuat.
   */
  if (phase === 'booting') {
    return (
      <div className="boot">
        <Loading label="Starting…" />
      </div>
    );
  }

  if (!authenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        {/* Orice alta ruta duce la login. Nu se retine unde voia sa ajunga:
            dupa autentificare rutarea porneste oricum din catalog, iar o
            redirectare pastrata peste o sesiune noua e o sursa buna de bucle. */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <CountsProvider>
      <ErrorBoundary>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/catalog" element={<CatalogScreen />} />
          <Route path="/catalog/:id" element={<CatalogItemScreen />} />
          <Route path="/cart" element={<CartScreen />} />
          <Route path="/requests" element={<RequestsScreen />} />
          <Route path="/requests/:id" element={<RequestDetailScreen />} />
          {/* Formularul „Cerere": un tichet liber, fara cerere de magazin in
              spate - de asta nu sta sub /requests, care sunt altceva. */}
          <Route path="/tickets/new" element={<NewTicketScreen />} />
          {/* Dupa `new`, ca `new` sa nu fie citit ca id - aceeasi conventie ca
              in rutele api-ului. `:id` prinde oricum orice text, deci ordinea e
              cea care le tine deosebite. */}
          <Route path="/tickets/:id" element={<TicketDetailScreen />} />
          {/* Lista, inaintea caii cu segment variabil - aceeasi conventie ca
              in rutele api-ului. */}
          <Route path="/help" element={<HelpScreen />} />
          {/* Articolul are ruta lui, nu o fereastra: cine urmeaza cinci pasi pe
              ecran vrea sa poata da inapoi, sa trimita legatura unui coleg, sau
              s-o deschida a doua oara maine. */}
          <Route path="/help/:id" element={<HelpArticleScreen />} />
          {/* Nu exista ruta /approvals si nici redirectare catre ceva in locul
              ei. Aprobarea s-a mutat pe tichet, in platforma, deci un semn de
              carte vechi cade pe `*` si ajunge in catalog - ca orice adresa care
              nu mai inseamna nimic aici. O redirectare catre /requests ar
              sugera ca ecranul s-a mutat, cand de fapt a disparut. */}
          <Route path="*" element={<Navigate to="/catalog" replace />} />
        </Route>
      </Routes>
      </ErrorBoundary>
    </CountsProvider>
  );
}
